import { optimizedRetryOption } from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { NetworkingDetails, NetworkingDetailsLabels } from "../../components/NetworkingDetails/NetworkingDetails";
import { DetailsPanelId, PageId } from "../../constants/pluginConstants";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { AzureVirtualNetworkCollection, AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { MdsDbSystem } from "../../gen/clients/mchub-azure-api-client-mds";
import { parseId } from "../../helpers/idHelper";
import { responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

export interface MysqlNetworkingDetailsProps {
  mdsDbSystem: MdsDbSystem | undefined;
  location: string;
  subscriptionId: string;
  resourceGroupName: string,
}

export const MysqlNetworkingDetails = (
  { mdsDbSystem, location, subscriptionId, resourceGroupName }: MysqlNetworkingDetailsProps,
): JSX.Element => {
  const { response: resNetwork } = useQueryCall({
    wait: !mdsDbSystem?.networkLinkId,
    method: apiClients.withRegion(getOciRegion(location)).netLinkApi.getNetworkLink,
    options: {
      args: {
        networkLinkName: decodeURIComponent(parseId(mdsDbSystem?.networkLinkId)?.resourceName || ""),
        subscriptionId: subscriptionId || "",
        resourceGroupName,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadNetLink(),
      },
    },
  });
  const networkLink = resNetwork?.data;

  const { response: vnetResponse } = useQueryCall({
    wait: !(subscriptionId && location),
    method: apiClients.withRegion(getOciRegion(location)).azResourceApi.listAzureVirtualNetworksBySubscriptionOnly,
    options: {
      args: { subscriptionId, location },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVNET(),
      },
    },
  });

  const vnets = (vnetResponse
    && responseItemstoArray<AzureVirtualNetworkCollection, AzureVirtualNetworkSummary>(vnetResponse)) || [];

  const selectedVnet = vnets?.find(vnet => vnet.id === networkLink?.properties?.azureAttachedNetworkIds?.[0]);
  const hostNameLabel: NetworkingDetailsLabels = { hostName: Messages.labels.hostNameOnly() };

  return (
    <NetworkingDetails
      labels={hostNameLabel}
      hostName={mdsDbSystem?.hostnameLabel}
      vnet={selectedVnet}
      networkLink={networkLink}
      ipAddress={mdsDbSystem?.ipAddress}
      location={location}
      mcvcnId={mdsDbSystem?.mcvcnId}
      analytics={{ pageId: PageId.MYSQL_DETAILS, panelId: DetailsPanelId.NETWORKING }}
    />
  );
};
